import './Countdown.css';
import { t } from '../../copy/strings';
import { fmtCountdown } from '../../format/time';
import { type CountdownVariant, countdownVariant } from '../../model/headline';
import { epochMs, minuteOf, secondsUntil, T_OPEN } from '../../model/time';
import { useConfig, useNow } from '../../state/hooks';

/** Variantes de §10.8 bloco 4 (`simulation` tem precedência sobre as outras três, em qualquer data). */
export type { CountdownVariant };

export interface CountdownState {
  variant: CountdownVariant;
  /** Segundos inteiros até 17h00:00 (arredondados para cima; só a variante `today` os mostra). */
  seconds: number;
}

/**
 * Variante da contagem (§10.8) para o relógio da fonte `nowMs`, pela data de calendário de Brasília (UTC−3 fixo, nunca
 * o fuso do aparelho): antes de 24/10 `days`; em 24/10 `tomorrow`; em 25/10 antes das 17h00 `today`; `simulation` com
 * `mode = 'simulation'`. `null` a partir das 17h00:00 do dia da eleição (a manchete passa ao estado seguinte no mesmo
 * tique, sem "0:00"; AC-10.8-03).
 */
export function countdownState(
  nowMs: number,
  electionDate: string,
  mode: 'live' | 'simulation',
): CountdownState | null {
  const epoch = epochMs(electionDate);
  const tMin = minuteOf(nowMs, epoch);
  if (tMin >= T_OPEN) return null;
  // Uma regra só para a variante: a de model/headline.ts, que também dá a chave de transição `countdown:{variante}`.
  return { variant: countdownVariant(tMin, mode === 'simulation'), seconds: secondsUntil(T_OPEN, nowMs, epoch) };
}

/**
 * Texto do leitor de tela (`countdown.sr`, §10.8 bloco 7): depende só dos minutos inteiros que faltam, então muda no
 * máximo uma vez por minuto (AC-10.8-04): "2 horas e 5 minutos" · "1 hora" · "5 minutos" · "menos de 1 minuto".
 */
export function countdownSr(seconds: number): string {
  const minutes = Math.floor(Math.max(0, seconds) / 60);
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (minutes === 0) return t('countdown.sr.lessThanMinute');
  if (h > 0 && m > 0) return t('countdown.sr', { N: h, M: m });
  if (h > 0) return t('countdown.sr.hours', { N: h });
  return t('countdown.sr.minutes', { M: m });
}

const PHRASE = {
  days: 'countdown.days',
  tomorrow: 'countdown.tomorrow',
  today: 'countdown.today',
  simulation: 'countdown.simulation',
} as const;

/**
 * Contagem regressiva para as 17h00 (§10.8), dentro das duas linhas reservadas da `Headline` (§7.4), com a tipografia
 * dela. Linha 1: frase; linha 2 (só `today`): o relógio em `<span role="timer">` sem `aria-live`, dígitos `aria-hidden`
 * e cópia `.sr-only` por minuto. Acompanha o tique de `useNow()` (relógio da fonte, alinhado ao segundo); a partir das
 * 17h00:00 mostra a frase de abertura (`headline.opened`, §13.2) até a manchete trocar de estado, nunca "0:00".
 */
export function Countdown() {
  const now = useNow();
  const cfg = useConfig();
  const st = countdownState(now.ms, cfg.election_date, cfg.mode);
  if (st === null) {
    return (
      <span class="countdown" data-testid="countdown" data-variant="open">
        <span class="countdown__line">{t('headline.opened')}</span>
      </span>
    );
  }
  return (
    <span class="countdown" data-testid="countdown" data-variant={st.variant}>
      <span class="countdown__line">{t(PHRASE[st.variant])}</span>
      {st.variant === 'today' && (
        <span class="countdown__line countdown__clock">
          <span class="countdown__timer" role="timer">
            <span aria-hidden="true">{t('countdown.timer', { 'H:MM:SS': fmtCountdown(st.seconds) })}</span>
            <span class="sr-only">{countdownSr(st.seconds)}</span>
          </span>
        </span>
      )}
    </span>
  );
}
